/**
 * @fileoverview Event Utilities - Custom event helpers and listeners
 * @version 0.1.0
 * @license MIT
 * 
 * Provides utilities for dispatching custom events, one-time event listeners,
 * and bound event handlers for Web Components.
 * 
 * USAGE:
 * ```javascript
 * import { dispatchEvent, listenOnce } from '@cliftontechlabs/ui-kit/utils/core';
 * 
 * // Dispatch custom event
 * dispatchEvent(element, 'value-changed', { value: 42 });
 * 
 * // Listen once
 * listenOnce(button, 'click', () => console.log('Clicked!'));
 * ```
 * 
 * @example
 * // Web Component event dispatch
 * class MyComponent extends HTMLElement {
 *   notify() {
 *     dispatchEvent(this, 'notification', { message: 'Hello' });
 *   }
 * }
 */

/**
 * Dispatch a custom event from an element with optional detail data
 * @param {EventTarget} element - The event target to dispatch the event from
 * @param {string} eventName - The event name
 * @param {any} [detail=null] - Optional data to pass in event.detail
 * @param {boolean} [bubbles=true] - Whether the event bubbles up through the DOM
 * @param {boolean} [cancelable=false] - Whether the event is cancelable
 * @param {boolean} [composed=true] - Whether the event crosses shadow DOM boundaries
 * @returns {boolean} True if the event was not canceled, false if it was canceled
 * 
 * @example
 * dispatchEvent(this, 'item-selected', { id: 123 });
 */
export function dispatchEvent(element, eventName, detail = null, bubbles = true, cancelable = false, composed = true) {
  const event = new CustomEvent(eventName, {
    detail,
    bubbles,
    cancelable,
    composed
  });
  return element.dispatchEvent(event);
}

/**
 * Helper to listen for a custom event once, then auto-remove the listener
 * @param {HTMLElement|Document|Window} target - Event target to listen on
 * @param {string} eventName - Event name to listen for
 * @param {Function} callback - Event handler callback
 */
export function listenOnce(target, eventName, callback) {
  function handler(event) {
    callback(event);
    target.removeEventListener(eventName, handler);
  }
  target.addEventListener(eventName, handler);
}

/**
 * Helper to add event listeners with automatic `this` binding to the given context
 * @param {HTMLElement|Document|Window} target - Element to add listener on
 * @param {string} eventName - Event name
 * @param {Function} handler - Handler function
 * @param {Object} context - The `this` context for handler
 * @param {Object} [options] - Optional event listener options
 */
export function addBoundEventListener(target, eventName, handler, context, options) {
  const boundHandler = handler.bind(context);
  target.addEventListener(eventName, boundHandler, options);
  return () => target.removeEventListener(eventName, boundHandler, options);
}
